
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import { useBearHoney } from '../../context/HoneyFarmContext';
import { formatHoney } from '../../helpers/formatters';
import HoneyCostPopup from './HoneyCostPopup';
import HoneyDContract from '../../helpers/HONEYDContract';
import config from '../../config';

// UI helpers
export const BEAR_STATUS = {
	CLAIMABLE: 'Hive Claimable',
	INELIGIBLE: 'Hive Claimed',
	UNKNOWN: 'Unknown',
};

const BearStatusBadge = ({ status }: { status: string }) => {
	const color = status === BEAR_STATUS.CLAIMABLE 
		? 'border-[#fcd34d] text-[#fcd34d]' 
		: status === BEAR_STATUS.UNKNOWN 
			? 'border-[#c4f6f9] text-[#c4f6f9]'
			: 'border-gray-500 text-gray-400';
	return <span className={`px-2 py-1 rounded-md text-xs bg-black bg-opacity-50 border ${color}`}>{status}</span>;
};

interface BearRowProps {
	bear: {
		id: number;
		image: string;
		honey: number;
		status: string;
	};
	selected: boolean;
	onSelect: (id: number) => void;
	onClaimHive?: (id: number) => Promise<void>;
	onCheckStatus?: (id: number) => Promise<void>;
}

const 	BearRow = ({ bear, selected, onSelect, onClaimHive, onCheckStatus }: BearRowProps) => {
	const [isClaimingHive, setIsClaimingHive] = useState(false);


	return (
		<>
			<div className={`flex items-center justify-between p-4 mb-4 bg-black bg-opacity-60 text-white shadow-md rounded-md border ${selected ? 'border-[#ffa2f8]' : 'border-[#55bac3]'}`}>
				<div className="flex items-center gap-4">
					<img 
						src={bear.image} 
						alt={`Bear #${bear.id}`} 
						className="w-16 h-16 rounded-md shadow-md" 
					/>
					<div>
						<div className="text-sm text-[#c4f6f9] mb-1">#{bear.id}</div>
						<div className="flex items-center gap-2">
							<BearStatusBadge status={bear.status} />
							{bear.status === BEAR_STATUS.CLAIMABLE && (
								<button
									onClick={async () => {
										if (!onClaimHive) return;
										setIsClaimingHive(true);
										try {
											await onClaimHive(bear.id);
										} finally {
											setIsClaimingHive(false);
										}
									}}
									disabled={isClaimingHive}
									className="px-3 py-1 rounded-md transition-colors bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3]/10 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isClaimingHive ? 'Claiming...' : 'Claim Hive'}
								</button>
							)}
						</div>
					</div>
				</div>
				<div className="flex items-center gap-6">
					<span className="font-mono text-sm text-[#fcd34d]">{formatHoney(bear.honey, 0)} HONEY</span>
					<input 
						type="checkbox" 
						checked={selected} 
						onChange={() => onSelect(bear.id)} 
						className="w-6 h-6 rounded border-2 border-white checked:bg-black checked:bg-opacity-50 checked:border-white transition-colors"
					/>
				</div>
			</div>
		</>
	);
};

const FILTERS = [BEAR_STATUS.UNKNOWN, BEAR_STATUS.CLAIMABLE, BEAR_STATUS.INELIGIBLE];

interface BearHoneyProps {
  signer?: ethers.Signer;
  address?: string;
}

const BearHoney: React.FC<BearHoneyProps> = ({ signer, address }) => {
  const { 
    bears,
    honey: totalBearHoney,
    selected,
    loading,
    refreshStates: refreshBearStates,
    select: selectBear,
    clearSelected: clearSelectedBears,
    checkHiveStatus,
    honeyFarmQueen
  } = useBearHoney();
  
  const [filter, setFilter] = useState<string | null>(null);
  const [searchId, setSearchId] = useState<string>('');
  const [claiming, setClaiming] = useState(false);
  const [honeyBalance, setHoneyBalance] = useState<number>(0);

  // Read provider - bypasses MetaMask for read operations
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Read contract (uses direct RPC to bypass MetaMask rate limits)
  const honeyReadContract = useMemo(() => {
    return new HoneyDContract(readProvider);
  }, [readProvider]);

  // Load honey balance
  const loadHoneyBalance = useCallback(async () => {
    if (!honeyReadContract || !address) return;
    
    try {
      const balance = await honeyReadContract.balanceOf(address);
      const formattedBalance = parseFloat(ethers.utils.formatEther(balance));
      setHoneyBalance(formattedBalance);
    } catch (error) {
      console.error('Error loading honey balance:', error);
      setHoneyBalance(0);
    }
  }, [honeyReadContract, address]);

  useEffect(() => {
    loadHoneyBalance();
  }, [loadHoneyBalance]);

  const bearCount = bears.length;
  const totalHoney = useMemo(() => bears.filter(b => selected.includes(b.id)).reduce((sum, b) => sum + b.honey, 0), [bears, selected]);
  
  const filteredBears = useMemo(() => {
    let filtered = bears;
    if (filter) {
      filtered = filtered.filter(b => b.status === filter);
    }
    if (searchId.trim()) {
      filtered = filtered.filter(b => b.id.toString().includes(searchId.trim()));
    }
    return filtered;
  }, [bears, filter, searchId]);

  const toggleSelect = (id: number) => {
    if (selected.includes(id)) {
      clearSelectedBears();
    } else {
      selectBear(id);
    }
  };

  const handleSelectAll = () => {
    filteredBears.forEach(bear => selectBear(bear.id));
  };

  const handleClearAll = () => {
    clearSelectedBears();
  };

  const handleClaim = async () => {
    if (!selected.length || !honeyFarmQueen) return;
    
    setClaiming(true);
    try {
      const tx = await honeyFarmQueen.claimBearsHoney(selected);
      if (tx && typeof tx.wait === 'function') {
        await tx.wait();
      }
      clearSelectedBears();
    } catch (error) {
      console.error("Error claiming honey:", error);
    }
    setClaiming(false);
    refreshBearStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  };

  const handleClaimHive = async (bearId: number) => {
    if (!honeyFarmQueen) return;
    try {
      const tx = await honeyFarmQueen.mintHive(bearId);
      await tx.wait(); // Wait for transaction to be mined
    } catch (error: any) {
      console.error("Error claiming hive:", error);
      // If already minted, we'll refresh anyway below
      if (!error?.message?.includes('Already minted') && !error?.reason?.includes('Already minted')) {
        throw error;
      }
    }
    // Refresh in background after button state resets
    refreshBearStates().catch(console.error);
  };

  const handleClaimAll = async () => {
    if (!bears.length || !honeyFarmQueen) return;
    
    setClaiming(true);
    try {
      const bearIds = bears.map(bear => bear.id);
      if (bearIds.length > 0) {
        const tx = await honeyFarmQueen.claimBearsHoney(bearIds);
        await tx.wait(); // Wait for transaction to be mined
      }
    } catch (error) {
      console.error("Error claiming all honey:", error);
    }
    // Always reset claiming state
    setClaiming(false);
    // Refresh in background after modal closes
    refreshBearStates().catch(console.error);
    loadHoneyBalance().catch(console.error);
  };

	return (
		<div className="max-w-4xl mx-auto p-4">
			<div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#c4f6f9]">
				<div>
					<h1 className="text-xl font-bold text-[#c4f6f9] mb-2">My Bears</h1>
					<div className="text-gray-300 text-sm">
						Bear Count: <span className="text-white">{bearCount}</span>
						<span className="mx-3">|</span>
						Total Claimable: <span className="text-[#fcd34d]">{formatHoney(totalBearHoney, 0)} HONEY</span>
						<span className="mx-3">|</span>
						Balance: <span className="text-[#fcd34d]">{honeyBalance.toFixed(2)} $HONEYD</span>
					</div>
				</div>
				<div className="flex flex-col xl:flex-row xl:justify-center items-center space-y-4 xl:space-y-0 xl:space-x-4 mt-4">
					<button 
						className="py-2 px-4 bg-transparent text-[#c4f6f9] border-2 border-[#c4f6f9] whitespace-nowrap rounded-md w-full xl:w-auto hover:bg-[#c4f6f9]/10 transition-colors text-sm" 
						onClick={handleSelectAll}
					>
						Select All
					</button>
					<button 
						className="py-2 px-4 bg-transparent text-[#c4f6f9] border-2 border-[#c4f6f9] whitespace-nowrap rounded-md w-full xl:w-auto hover:bg-[#c4f6f9]/10 transition-colors text-sm" 
						onClick={handleClearAll}
					>
						Clear
					</button>
					<HoneyCostPopup
						requiredHoney={totalHoney}
						addressHoney={honeyBalance}
						callback={handleClaim}
						callBackArgs={[]}
						claim={true}
						trigger={
							<button 
								className="py-2 px-4 bg-transparent text-[#55bac3] whitespace-nowrap rounded-md w-full xl:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-[#55bac3]/10 border-2 border-[#55bac3] text-sm"
								disabled={selected.length === 0 || claiming} 
							>
								{claiming ? 'Claiming...' : `Claim Selected (${formatHoney(totalHoney, 0)})`}
							</button>
						}
					/>
					<HoneyCostPopup
						requiredHoney={totalBearHoney}
						addressHoney={honeyBalance}
						callback={handleClaimAll}
						callBackArgs={[]}
						claim={true}
						trigger={
							<button 
								className="py-2 px-4 bg-transparent text-[#55bac3] whitespace-nowrap rounded-md w-full xl:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-[#55bac3]/10 border-2 border-[#55bac3] text-sm"
								disabled={claiming} 
							>
								{claiming ? 'Claiming...' : 'Claim All'}
							</button>
						}
					/>
				</div>
			</div>
			<div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#55bac3]">
				<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
					<div className="flex items-center gap-3 flex-wrap">
						<span className="text-sm text-white">Filter:</span>
						{FILTERS.map(f => (
							<button 
								key={f} 
								className={`px-3 py-1 rounded-md transition-colors text-sm ${
									filter === f 
									? 'bg-transparent border-2 text-[#ffa2f8] border-[#ffa2f8]' 
									: 'bg-transparent border-2 text-[#c4f6f9] border-[#c4f6f9] hover:bg-[#c4f6f9]/10'
								}`} 
								onClick={() => setFilter(filter === f ? null : f)}
							>
								{f}
							</button>
						))}
						<button 
							className={`px-3 py-1 rounded-md transition-colors text-sm ${
								filter === null
								? 'bg-transparent border-2 text-[#ffa2f8] border-[#ffa2f8]'
								: 'bg-transparent border-2 text-[#c4f6f9] border-[#c4f6f9] hover:bg-[#c4f6f9]/10'
							}`}
							onClick={() => setFilter(null)}
						>
							All
						</button>
					</div>
					<div className="flex items-center gap-3 min-w-[200px]">
						<span className="text-sm text-white whitespace-nowrap">Search ID:</span>
						<input
							type="text"
							value={searchId}
							onChange={(e) => setSearchId(e.target.value.replace(/[^0-9]/g, ''))}
							placeholder="Enter bear ID"
							className="px-3 py-1 rounded-md bg-transparent border-2 border-[#55bac3] text-white placeholder-gray-400 focus:outline-none focus:border-[#ffa2f8] w-full text-sm"
						/>
					</div>
				</div>
			</div>
			<div className="rounded-lg overflow-hidden">
				{loading ? (
					<div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
						<div className="animate-pulse text-[#c4f6f9]">Loading bears...</div>
					</div>
				) : filteredBears.length === 0 ? (
					<div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-gray-700">
						<div className="text-gray-400">No bears found.</div>
					</div>
				) : filteredBears.map(bear => (
					<BearRow 
						key={bear.id} 
						bear={bear} 
						selected={selected.includes(bear.id)} 
						onSelect={toggleSelect}
						onClaimHive={handleClaimHive}
						onCheckStatus={checkHiveStatus}
					/>
				))}
			</div>

			{/* Processing Overlay */}
			{claiming && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
					<div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
						<div className="text-[#ffa2f8]">Processing transaction...</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default BearHoney;
